import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSpecializations,
  nextStep,
  prevStep,
  selectSpecializations,
  selectClinicInfo,
} from "../../../store/slices/registrationSlice";
import { Target, ArrowLeft, ArrowRight, Check, Plus } from "@phosphor-icons/react";
import CustomClinikitButton from "../../ui/CustomClinikitButton";
import {
  SUB_SPECIALIZATIONS_BY_TYPE,
  AGE_RANGE_OPTIONS,
  getPredefinedSubValues,
} from "../../../data/clinicTypesConfig";
import "./Steps.css";

function SpecializationsStep() {
  const dispatch = useDispatch();
  const savedSpecs = useSelector(selectSpecializations);
  const clinicInfo = useSelector(selectClinicInfo);

  const [formData, setFormData] = useState({
    subSpecializations: savedSpecs.subSpecializations || [],
    ageRange: savedSpecs.ageRange || [],
  });

  const availableSubSpecs =
    SUB_SPECIALIZATIONS_BY_TYPE[clinicInfo.clinicType] || [];

  // "Other" custom sub-specialization state
  const [isOtherActive, setIsOtherActive] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const [otherError, setOtherError] = useState("");

  const predefinedValues = getPredefinedSubValues(clinicInfo.clinicType);

  const toggleSubSpecialization = (value) => {
    setFormData((prev) => ({
      ...prev,
      subSpecializations: prev.subSpecializations.includes(value)
        ? prev.subSpecializations.filter((s) => s !== value)
        : [...prev.subSpecializations, value],
    }));
  };

  const toggleAgeRange = (value) => {
    setFormData((prev) => ({
      ...prev,
      ageRange: prev.ageRange.includes(value)
        ? prev.ageRange.filter((a) => a !== value)
        : [...prev.ageRange, value],
    }));
  };

  const handleAddCustomSub = () => {
    const trimmed = otherValue.trim();
    setOtherError("");

    if (trimmed.length < 2) {
      setOtherError("מינימום 2 תווים");
      return;
    }
    if (trimmed.length > 50) {
      setOtherError("מקסימום 50 תווים");
      return;
    }

    const allExistingValues = [
      ...Array.from(predefinedValues),
      ...formData.subSpecializations,
    ];
    const allExistingLabels = availableSubSpecs.map((s) => s.label);

    if (
      allExistingValues.includes(trimmed) ||
      allExistingLabels.includes(trimmed) ||
      formData.subSpecializations.includes(trimmed)
    ) {
      setOtherError("התמחות זו כבר קיימת");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      subSpecializations: [...prev.subSpecializations, trimmed],
    }));
    setOtherValue("");
    setIsOtherActive(false);
  };

  const getSubLabel = (value) => {
    const predefined = availableSubSpecs.find((s) => s.value === value);
    return predefined ? predefined.label : value;
  };

  const handleBack = () => {
    dispatch(
      setSpecializations({
        specializations: [clinicInfo.clinicType],
        subSpecializations: formData.subSpecializations,
        ageRange: formData.ageRange,
      })
    );
    dispatch(prevStep());
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(
      setSpecializations({
        specializations: [clinicInfo.clinicType],
        subSpecializations: formData.subSpecializations,
        ageRange: formData.ageRange,
      })
    );
    dispatch(nextStep());
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <Target size={24} weight="duotone" className="step-icon" />
        <h2>התמחויות</h2>
        <p>בחרו את תחומי ההתמחות וקבוצות הגיל שלכם</p>
      </div>

      <form onSubmit={handleSubmit} className="step-form">
        <div className="form-group">
          <label>
            תתי-התמחויות
            <span className="optional-tag">לא חובה</span>
          </label>
          <p className="field-description">
            בחרו את התחומים הספציפיים בהם אתם מתמחים. זה יעזור להציע תבניות
            סיכום רלוונטיות.
          </p>
          <div className="chips-container">
            {availableSubSpecs.map((spec) => (
              <button
                key={spec.value}
                type="button"
                className={`chip ${
                  formData.subSpecializations.includes(spec.value)
                    ? "selected"
                    : ""
                }`}
                onClick={() => toggleSubSpecialization(spec.value)}
              >
                {formData.subSpecializations.includes(spec.value) && (
                  <Check size={14} weight="bold" className="chip-check" />
                )}
                <span>{spec.label}</span>
              </button>
            ))}

            {/* Custom (user-added) sub-specializations */}
            {formData.subSpecializations
              .filter((s) => !predefinedValues.has(s))
              .map((customSub) => (
                <button
                  key={customSub}
                  type="button"
                  className="chip selected"
                  onClick={() => toggleSubSpecialization(customSub)}
                >
                  <Check size={14} weight="bold" className="chip-check" />
                  <span>{customSub}</span>
                </button>
              ))}

            {/* "Other" chip / input */}
            {!isOtherActive ? (
              <button
                type="button"
                className="chip chip-other"
                onClick={() => setIsOtherActive(true)}
              >
                <Plus size={14} weight="bold" />
                <span>אחר</span>
              </button>
            ) : (
              <div className="chip-other-input-wrapper">
                <input
                  type="text"
                  className={`chip-other-input ${otherError ? "error" : "input-rtl-direction"}`}
                  value={otherValue}
                  onChange={(e) => {
                    setOtherValue(e.target.value);
                    setOtherError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCustomSub();
                    }
                    if (e.key === "Escape") {
                      setIsOtherActive(false);
                      setOtherValue("");
                      setOtherError("");
                    }
                  }}
                  placeholder="הקלידו התמחות..."
                  autoFocus
                  maxLength={50}
                />
                <button
                  type="button"
                  className="chip-other-add-btn"
                  onClick={handleAddCustomSub}
                >
                  הוסף
                </button>
                {otherError && (
                  <span className="chip-other-error">{otherError}</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>
            קבוצות גיל
            <span className="optional-tag">לא חובה</span>
          </label>
          <p className="field-description">עם אילו קבוצות גיל אתם עובדים?</p>
          <div className="chips-container">
            {AGE_RANGE_OPTIONS.map((age) => (
              <button
                key={age.value}
                type="button"
                className={`chip ${
                  formData.ageRange.includes(age.value) ? "selected" : ""
                }`}
                onClick={() => toggleAgeRange(age.value)}
              >
                {formData.ageRange.includes(age.value) && (
                  <Check size={14} weight="bold" className="chip-check" />
                )}
                <span>{age.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="step-actions step-actions-between">
          <CustomClinikitButton
            type="button"
            variant="ghost"
            size="medium"
            onClick={handleBack}
          >
            <ArrowRight size={18} weight="bold" />
            חזרה
          </CustomClinikitButton>
          <CustomClinikitButton type="submit" variant="primary" size="large">
            המשך
            <ArrowLeft size={18} weight="bold" />
          </CustomClinikitButton>
        </div>
      </form>
    </div>
  );
}

export default SpecializationsStep;
